/* iss-pass-finder.js
   © 2026 Ontario Telescope and Accessories Inc. All rights reserved.
   Ontario Telescope -- ISS Pass Finder
   Requires: iss-satellite.min.js, iss-land-data.js
*/
(function(){
'use strict';
var LAND = window.ISS_LAND || [];
var D2R=Math.PI/180, R2D=180/Math.PI, RE=6378.137, R_SUN=695700, AU=149597870.7;

/* Optional local backup element set. Leave null unless something refreshes
   it every week or two -- a stale set produces confidently wrong times,
   which is worse than an honest error message. */
var BACKUP_TLE = null;

/* =========================== ASTRONOMY CORE =========================== */
function jday(d){ return d.getTime()/86400000 + 2440587.5; }
function sunEci(d){
  var n=jday(d)-2451545.0;
  var L=(280.460+0.9856474*n)%360;
  var g=((357.528+0.9856003*n)%360)*D2R;
  var lam=(L+1.915*Math.sin(g)+0.020*Math.sin(2*g))*D2R;
  var eps=(23.439-0.0000004*n)*D2R;
  var R=(1.00014-0.01671*Math.cos(g)-0.00014*Math.cos(2*g))*AU;
  return {x:R*Math.cos(lam), y:R*Math.cos(eps)*Math.sin(lam), z:R*Math.sin(eps)*Math.sin(lam)};
}
function isSunlit(p,s){
  var rs=Math.sqrt(p.x*p.x+p.y*p.y+p.z*p.z);
  var dx=s.x-p.x, dy=s.y-p.y, dz=s.z-p.z, ds=Math.sqrt(dx*dx+dy*dy+dz*dz);
  var c=(-p.x*dx-p.y*dy-p.z*dz)/(rs*ds); c=Math.max(-1,Math.min(1,c));
  return Math.acos(c) > (Math.asin(Math.min(1,(RE+20)/rs)) - Math.asin(Math.min(1,R_SUN/ds)));
}
function magnitude(range, phase, std){
  var f=((Math.PI-phase)*Math.cos(phase)+Math.sin(phase))/Math.PI;
  if(f<1e-6) f=1e-6;
  return std + 5*Math.log10(range/1000) - 2.5*Math.log10(f);
}
var COMPASS=['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
var LONGDIR={N:'north',NNE:'north-northeast',NE:'northeast',ENE:'east-northeast',E:'east',ESE:'east-southeast',SE:'southeast',SSE:'south-southeast',S:'south',SSW:'south-southwest',SW:'southwest',WSW:'west-southwest',W:'west',WNW:'west-northwest',NW:'northwest',NNW:'north-northwest'};
function compass(a){ return COMPASS[Math.round((((a%360)+360)%360)/22.5)%16]; }

function sampleAt(satrec, obs, d, std){
  var pv=satellite.propagate(satrec,d);
  if(!pv||!pv.position) return null;
  var g=satellite.gstime(d);
  var look=satellite.ecfToLookAngles(obs, satellite.eciToEcf(pv.position,g));
  var sE=sunEci(d);
  var sLook=satellite.ecfToLookAngles(obs, satellite.eciToEcf(sE,g));
  var obsEci=satellite.ecfToEci(satellite.geodeticToEcf(obs), g);
  var a=[sE.x-pv.position.x,sE.y-pv.position.y,sE.z-pv.position.z];
  var b=[obsEci.x-pv.position.x,obsEci.y-pv.position.y,obsEci.z-pv.position.z];
  var na=Math.sqrt(a[0]*a[0]+a[1]*a[1]+a[2]*a[2]), nb=Math.sqrt(b[0]*b[0]+b[1]*b[1]+b[2]*b[2]);
  var cp=Math.max(-1,Math.min(1,(a[0]*b[0]+a[1]*b[1]+a[2]*b[2])/(na*nb)));
  var gd=satellite.eciToGeodetic(pv.position,g);
  return { t:d.getTime(), el:look.elevation*R2D, az:look.azimuth*R2D, range:look.rangeSat,
    sunEl:sLook.elevation*R2D, lit:isSunlit(pv.position,sE), altKm:gd.height,
    mag:magnitude(look.rangeSat, Math.acos(cp), std) };
}
function liveState(satrec, d, obs){
  var pv=satellite.propagate(satrec,d);
  if(!pv||!pv.position) return null;
  var g=satellite.gstime(d), gd=satellite.eciToGeodetic(pv.position,g);
  var o={ lat:satellite.degreesLat(gd.latitude), lon:satellite.degreesLong(gd.longitude),
    altKm:gd.height,
    speedKmS:Math.sqrt(pv.velocity.x*pv.velocity.x+pv.velocity.y*pv.velocity.y+pv.velocity.z*pv.velocity.z),
    lit:isSunlit(pv.position,sunEci(d)) };
  if(obs){
    var l=satellite.ecfToLookAngles(obs, satellite.eciToEcf(pv.position,g));
    o.el=l.elevation*R2D; o.az=l.azimuth*R2D; o.range=l.rangeSat;
  }
  return o;
}
function findPasses(satrec, obs, start, end, opt){
  opt=opt||{};
  var minEl=opt.minEl!=null?opt.minEl:10, sunMax=opt.sunElMax!=null?opt.sunElMax:-6;
  var stepMs=(opt.stepSec||30)*1000, std=opt.stdMag!=null?opt.stdMag:-1.8;
  var out=[], cur=null;
  function elAt(ms){
    var d=new Date(ms), pv=satellite.propagate(satrec,d);
    if(!pv||!pv.position) return -90;
    return satellite.ecfToLookAngles(obs, satellite.eciToEcf(pv.position,satellite.gstime(d))).elevation*R2D;
  }
  function refine(a,b,target){
    for(var i=0;i<12 && (b-a)>500;i++){
      var m=(a+b)/2;
      if((elAt(a)-target)*(elAt(m)-target)<=0) b=m; else a=m;
    }
    return (a+b)/2;
  }
  function peakRefine(a,b){
    for(var i=0;i<26 && (b-a)>1000;i++){
      var m1=a+(b-a)/3, m2=b-(b-a)/3;
      if(elAt(m1)<elAt(m2)) a=m1; else b=m2;
    }
    return sampleAt(satrec,obs,new Date((a+b)/2),std);
  }
  function close(ss){
    var f=ss[0], l=ss[ss.length-1];
    var riseT=refine(f.t-stepMs, f.t, minEl), setT=refine(l.t, l.t+stepMs, minEl);
    var peak=ss[0]; for(var i=1;i<ss.length;i++) if(ss[i].el>peak.el) peak=ss[i];
    var rp=peakRefine(Math.max(riseT,peak.t-stepMs), Math.min(setT,peak.t+stepMs));
    if(rp && rp.el>peak.el) peak=rp;
    var vis=[]; for(var j=0;j<ss.length;j++) if(ss[j].lit && ss[j].sunEl<=sunMax) vis.push(ss[j]);
    var p={ rise:{t:riseT,az:f.az}, set:{t:setT,az:l.az},
      peak:{t:peak.t,az:peak.az,el:peak.el,range:peak.range,altKm:peak.altKm},
      durationSec:(setT-riseT)/1000, visible:vis.length>0, samples:ss };
    if(vis.length){
      var v0=vis[0], v1=vis[vis.length-1], vp=vis[0], mg=99, sMax=-99;
      for(var k=0;k<vis.length;k++){ if(vis[k].el>vp.el) vp=vis[k]; if(vis[k].mag<mg) mg=vis[k].mag; if(vis[k].sunEl>sMax) sMax=vis[k].sunEl; }
      if(vis.length>2){
        var rv=peakRefine(Math.max(v0.t,vp.t-stepMs), Math.min(v1.t,vp.t+stepMs));
        if(rv && rv.el>vp.el && rv.lit && rv.sunEl<=sunMax){ vp=rv; if(rv.mag<mg) mg=rv.mag; }
      }
      p.visStart={t:v0.t,az:v0.az,el:v0.el,dir:compass(v0.az)};
      p.visEnd={t:v1.t,az:v1.az,el:v1.el,dir:compass(v1.az)};
      p.visPeak={t:vp.t,az:vp.az,el:vp.el,range:vp.range,dir:compass(vp.az),altKm:vp.altKm};
      p.visDurationSec=(v1.t-v0.t)/1000+(opt.stepSec||30);
      p.mag=mg; p.maxSunEl=sMax;
      p.startReason=(v0.t-riseT)<1.6*stepMs?'rise':'shadow';
      p.endReason=(setT-v1.t)<1.6*stepMs?'set':'shadow';
      p.visSamples=vis;
    }
    return p;
  }
  for(var t=start; t<=end; t+=stepMs){
    var s=sampleAt(satrec,obs,new Date(t),std);
    if(!s) continue;
    if(s.el>=minEl){ if(!cur) cur=[]; cur.push(s); }
    else if(cur){ out.push(close(cur)); cur=null; }
  }
  if(cur) out.push(close(cur));
  return opt.allPasses ? out : out.filter(function(p){return p.visible;});
}

/* =========================== STATE =========================== */
var S={ satrec:null, tleInfo:null, loc:null, days:7, unit:'km', tzMode:'local',
  minEl:10, sunElMax:-6, allPasses:false, passes:[], liveTimer:null, pal:null };
var $=function(id){ return document.getElementById(id); };
var root=document.querySelector('.iss');

/* resolve CSS custom properties (which may be color-mix()) to rgb for canvas */
function palette(){
  if(S.pal) return S.pal;
  var probe=document.createElement('span');
  probe.style.cssText='position:absolute;visibility:hidden;pointer-events:none';
  root.appendChild(probe);
  function c(v,fb){
    probe.style.color=''; probe.style.color='var(--iss-'+v+')';
    var got=getComputedStyle(probe).color;
    return (got && got!=='rgba(0, 0, 0, 0)') ? got : fb;
  }
  S.pal={ paper:c('paper','#08090b'), card:c('card','#131417'), sunken:c('sunken','#1b1d22'),
    rule:c('rule','#34373d'), ruleSoft:c('rule-soft','#25272c'), ink:c('ink','#e9ebee'),
    inkSoft:c('ink-soft','#9aa1aa'), brand:c('brand','#ed0000'), accent:c('accent','#d69a4e'),
    ok:c('ok','#4fae7f') };
  root.removeChild(probe);
  return S.pal;
}

/* =========================== FORMAT =========================== */
function dist(km,dp){ dp=dp==null?0:dp; return S.unit==='km' ? Math.round(km).toLocaleString()+' km' : Math.round(km*0.621371).toLocaleString()+' mi'; }
function elevStr(m){ return S.unit==='km' ? Math.round(m)+' m' : Math.round(m*3.28084)+' ft'; }
function speed(kms){ return S.unit==='km' ? Math.round(kms*3600).toLocaleString()+' km/h' : Math.round(kms*3600*0.621371).toLocaleString()+' mph'; }
function tzName(){ return S.tzMode==='utc' ? 'UTC' : ((S.loc && S.loc.tz) || Intl.DateTimeFormat().resolvedOptions().timeZone); }
function shortTz(tz){ var p=tz.split('/'); return p[p.length-1].replace(/_/g,' '); }
function fmt(ms,sec){
  var o={timeZone:tzName(),hour:'2-digit',minute:'2-digit',hour12:false};
  if(sec) o.second='2-digit';
  return new Intl.DateTimeFormat('en-GB',o).format(new Date(ms));
}
function fmtUTC(ms,sec){
  var o={timeZone:'UTC',hour:'2-digit',minute:'2-digit',hour12:false};
  if(sec) o.second='2-digit';
  return new Intl.DateTimeFormat('en-GB',o).format(new Date(ms))+' UTC';
}
function dayParts(ms){
  var p=new Intl.DateTimeFormat('en-GB',{timeZone:tzName(),weekday:'short',day:'2-digit',month:'short'}).formatToParts(new Date(ms));
  var o={}; p.forEach(function(x){o[x.type]=x.value;}); return o;
}
function mmss(s){ s=Math.round(s); if(s<60) return s+' s'; var m=Math.floor(s/60), r=s%60; return m+' min '+(r<10?'0':'')+r+' s'; }
function brightness(m){
  if(m<=-3) return {label:'Dazzling',cls:'iss-ok'};
  if(m<=-2) return {label:'Very bright',cls:'iss-ok'};
  if(m<=-1) return {label:'Bright',cls:'iss-ok'};
  if(m<=0.5) return {label:'Moderate',cls:'iss-warn'};
  return {label:'Faint',cls:'iss-dim'};
}
function darkness(sunEl){
  if(sunEl>-6) return 'bright twilight';
  if(sunEl>-12) return 'civil twilight, still a glow in the west';
  if(sunEl>-18) return 'deep twilight';
  return 'fully dark sky';
}
function esc(s){ return String(s).replace(/[&<>"]/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c];}); }

/* =========================== TLE =========================== */
var TLE_SOURCES=[
  { name:'CelesTrak', url:'https://celestrak.org/NORAD/elements/gp.php?CATNR=25544&FORMAT=TLE',
    parse:function(t){ var l=t.trim().split(/\r?\n/); if(l.length<3||l[1][0]!=='1') return null; return {line1:l[1].trim(),line2:l[2].trim()}; } },
  { name:'TLE API', url:'https://tle.ivanstanojevic.me/api/tle/25544',
    parse:function(t){ var j=JSON.parse(t); return j&&j.line1?{line1:j.line1,line2:j.line2}:null; } },
  { name:'WhereTheISS.at', url:'https://api.wheretheiss.at/v1/satellites/25544/tles',
    parse:function(t){ var j=JSON.parse(t); return j&&j.line1?{line1:j.line1,line2:j.line2}:null; } }
];
function loadTLE(){
  var i=0;
  function next(){
    if(i>=TLE_SOURCES.length){
      if(BACKUP_TLE){
        S.satrec=satellite.twoline2satrec(BACKUP_TLE.line1,BACKUP_TLE.line2);
        S.tleInfo={source:'stored backup element set',stale:true,line1:BACKUP_TLE.line1,line2:BACKUP_TLE.line2};
      } else { S.tleInfo=null; }
      return Promise.resolve();
    }
    var s=TLE_SOURCES[i++];
    return fetch(s.url,{cache:'no-store'}).then(function(r){ if(!r.ok) throw 0; return r.text(); })
      .then(function(txt){
        var t=s.parse(txt); if(!t) throw 0;
        var rec=satellite.twoline2satrec(t.line1,t.line2);
        if(!rec || rec.error) throw 0;
        S.satrec=rec; S.tleInfo={source:s.name,line1:t.line1,line2:t.line2};
      }).catch(function(){ return next(); });
  }
  return next().then(function(){
    if(!S.tleInfo) return;
    var l1=S.tleInfo.line1, yr=parseInt(l1.substr(18,2),10), doy=parseFloat(l1.substr(20,12));
    yr += yr<57?2000:1900;
    S.tleInfo.epoch=new Date(Date.UTC(yr,0,1)+(doy-1)*86400000);
    S.tleInfo.ageDays=(Date.now()-S.tleInfo.epoch.getTime())/86400000;
  });
}

/* =========================== LOCATION =========================== */
function observerGd(){ return { longitude:S.loc.lon*D2R, latitude:S.loc.lat*D2R, height:(S.loc.elev||0)/1000 }; }
function setLoc(l){
  S.loc=l;
  $('iss-locnow').innerHTML='<b>'+esc(l.name)+(l.admin?', '+esc(l.admin):'')+(l.country?', '+esc(l.country):'')+
    '</b> &middot; '+l.lat.toFixed(3)+'&deg;, '+l.lon.toFixed(3)+'&deg;'+(l.elev?' &middot; '+elevStr(l.elev)+' above sea level':'');
  compute(); startLive();
}
var searchTimer=null, searchResults=[];
$('iss-q').addEventListener('input',function(){
  clearTimeout(searchTimer);
  var v=this.value.trim();
  if(v.length<2){ $('iss-res').style.display='none'; return; }
  searchTimer=setTimeout(function(){ doSearch(v); },260);
});
$('iss-q').addEventListener('blur',function(){ setTimeout(function(){ $('iss-res').style.display='none'; },180); });
$('iss-q').addEventListener('focus',function(){ if(searchResults.length) $('iss-res').style.display='block'; });
function doSearch(v){
  fetch('https://geocoding-api.open-meteo.com/v1/search?count=8&language=en&format=json&name='+encodeURIComponent(v))
   .then(function(r){return r.json();})
   .then(function(j){
     searchResults=j.results||[];
     var box=$('iss-res');
     if(!searchResults.length){ box.innerHTML='<div>No matching place found</div>'; box.style.display='block'; return; }
     box.innerHTML=searchResults.map(function(r,i){
       return '<div data-i="'+i+'">'+esc(r.name)+' <small>'+esc([r.admin1,r.country].filter(Boolean).join(', '))+'</small></div>';
     }).join('');
     box.style.display='block';
     Array.prototype.forEach.call(box.children,function(el){
       el.addEventListener('mousedown',function(){
         var r=searchResults[+el.getAttribute('data-i')];
         $('iss-q').value=r.name; box.style.display='none';
         setLoc({name:r.name,admin:r.admin1,country:r.country,lat:r.latitude,lon:r.longitude,elev:r.elevation,tz:r.timezone});
       });
     });
   }).catch(function(){});
}
$('iss-geo').addEventListener('click',function(){
  if(!navigator.geolocation){ $('iss-locnow').textContent='Your browser will not share a location. Type a town instead.'; return; }
  $('iss-locnow').innerHTML='<span class="iss-spin"></span>Asking your browser&hellip;';
  navigator.geolocation.getCurrentPosition(function(pos){
    var la=pos.coords.latitude, lo=pos.coords.longitude;
    setLoc({name:'Your location',admin:'',country:'',lat:la,lon:lo,elev:pos.coords.altitude||0,
      tz:Intl.DateTimeFormat().resolvedOptions().timeZone});
    fetch('https://api.bigdatacloud.net/data/reverse-geocode-client?latitude='+la+'&longitude='+lo+'&localityLanguage=en')
      .then(function(r){return r.json();}).then(function(j){
        if(!j) return;
        var nm=j.city||j.locality||j.principalSubdivision;
        if(nm){ S.loc.name=nm; S.loc.admin=j.principalSubdivision||''; S.loc.country=j.countryName||''; setLoc(S.loc); }
      }).catch(function(){});
  },function(){
    $('iss-locnow').textContent='Could not get your location (permission denied). Type a town above instead.';
  },{enableHighAccuracy:false,timeout:10000,maximumAge:600000});
});

/* =========================== CONTROLS =========================== */
function seg(id,fn){
  $(id).addEventListener('click',function(e){
    var b=e.target.closest('button'); if(!b) return;
    Array.prototype.forEach.call(this.children,function(c){ c.classList.remove('iss-on'); });
    b.classList.add('iss-on'); fn(b);
  });
}
seg('iss-range',function(b){ S.days=+b.getAttribute('data-days'); compute(); });
seg('iss-units',function(b){ S.unit=b.getAttribute('data-u'); render(); renderLive(); if(S.loc) setLocLine(); });
seg('iss-tz',function(b){ S.tzMode=b.getAttribute('data-tz'); render(); renderLive(); });
$('iss-minel').addEventListener('change',function(){ S.minEl=+this.value; compute(); });
$('iss-sunel').addEventListener('change',function(){ S.sunElMax=+this.value; compute(); });
$('iss-all').addEventListener('change',function(){ S.allPasses=this.checked; compute(); });
function setLocLine(){
  var l=S.loc;
  $('iss-locnow').innerHTML='<b>'+esc(l.name)+(l.admin?', '+esc(l.admin):'')+(l.country?', '+esc(l.country):'')+
    '</b> &middot; '+l.lat.toFixed(3)+'&deg;, '+l.lon.toFixed(3)+'&deg;'+(l.elev?' &middot; '+elevStr(l.elev)+' above sea level':'');
}

/* =========================== COMPUTE + RENDER =========================== */
function compute(){
  if(!S.satrec||!S.loc) return;
  $('iss-passes').innerHTML='<div class="iss-empty"><span class="iss-spin"></span>Propagating the orbit&hellip;</div>';
  setTimeout(function(){
    var now=Date.now();
    S.passes=findPasses(S.satrec, observerGd(), now, now+S.days*86400000,
      {minEl:S.minEl,sunElMax:S.sunElMax,allPasses:S.allPasses,stepSec:30});
    render(); renderFoot();
  },20);
}
function render(){
  var el=$('iss-passes');
  $('iss-passhead').textContent = S.allPasses ? 'All passes' : 'Visible passes';
  if(!S.loc){ return; }
  if(!S.passes.length){
    $('iss-count').textContent='';
    el.innerHTML='<div class="iss-empty"><b>No visible passes in the next '+S.days+' days.</b><br>'+
      'This is normal &mdash; the station drifts in and out of evening visibility on a roughly six-week cycle. '+
      'Try the month view, or lower the minimum height under Advanced.</div>';
    return;
  }
  $('iss-count').innerHTML=S.passes.length+' '+(S.allPasses?'passes':'visible passes')+' in the next '+S.days+
    ' days for '+esc(S.loc.name)+', times in '+(S.tzMode==='utc'?'UTC':shortTz(tzName()))+'.';

  var best=null;
  S.passes.forEach(function(p){ if(p.visible && (!best || p.visPeak.el>best.visPeak.el)) best=p; });

  el.innerHTML=S.passes.map(function(p,i){
    var vis=p.visible;
    var startT=vis?p.visStart.t:p.rise.t;
    var pk=vis?p.visPeak:p.peak;
    var dur=vis?p.visDurationSec:p.durationSec;
    var b=vis?brightness(p.mag):{label:'Not visible',cls:'iss-dim'};
    var dp=dayParts(startT);
    return '<div class="iss-pass'+(p===best?' iss-topbill':'')+'" data-i="'+i+'">'+
      '<button class="iss-phead" type="button" aria-expanded="false">'+
        '<span class="iss-pdate"><span class="iss-dd">'+dp.day+'</span><br><span class="iss-mm">'+dp.month+' &middot; '+dp.weekday+'</span></span>'+
        '<span>'+
          '<span class="iss-ptime">'+fmt(startT)+'<span class="iss-alt">'+fmtUTC(startT)+'</span></span>'+
          '<span class="iss-pmeta">In view <b>'+mmss(dur)+'</b> &middot; peaks <b>'+Math.round(pk.el)+'&deg;</b> in the <b>'+LONGDIR[compass(pk.az)]+'</b> &middot; appears <b>'+LONGDIR[vis?p.visStart.dir:compass(p.rise.az)]+'</b>, fades <b>'+LONGDIR[vis?p.visEnd.dir:compass(p.set.az)]+'</b></span>'+
        '</span>'+
        '<span class="iss-pflags">'+
          (p===best?'<span class="iss-flag iss-best">Best this '+(S.days>7?'month':'week')+'</span>':'')+
          '<span class="iss-flag '+b.cls+'">'+b.label+(vis?' &middot; mag '+p.mag.toFixed(1):'')+'</span>'+
          '<span class="iss-chev">Details +</span>'+
        '</span>'+
      '</button>'+
      '<div class="iss-pbody">'+passDetail(p)+'</div>'+
    '</div>';
  }).join('');

  Array.prototype.forEach.call(el.querySelectorAll('.iss-phead'),function(h){
    h.addEventListener('click',function(){
      var open=h.parentNode.classList.toggle('iss-open');
      h.setAttribute('aria-expanded',open?'true':'false');
      h.querySelector('.iss-chev').textContent = open?'Details –':'Details +';
    });
  });
}
function leg(label,t,az,elv,rng,note){
  return '<div class="iss-kpi">'+
    '<div class="iss-l">'+label+'</div>'+
    '<div class="iss-v iss-sm">'+fmt(t,true)+'</div>'+
    '<div class="iss-d">'+fmtUTC(t,true)+'</div>'+
    '<div class="iss-d">'+Math.round(elv)+'&deg; above the <b style="color:inherit">'+LONGDIR[compass(az)]+'</b> ('+Math.round(az)+'&deg;)</div>'+
    (rng!=null?'<div class="iss-d">'+dist(rng)+' away</div>':'')+
    (note?'<div class="iss-d" style="color:var(--iss-accent)">'+note+'</div>':'')+
  '</div>';
}
function passDetail(p){
  var vis=p.visible;
  var a=vis?p.visStart:{t:p.rise.t,az:p.rise.az,el:S.minEl};
  var c=vis?p.visEnd:{t:p.set.t,az:p.set.az,el:S.minEl};
  var pk=vis?p.visPeak:p.peak;
  var sNote=vis?(p.startReason==='shadow'?'emerges from Earth’s shadow':'rises into view'):'rises above '+S.minEl+'°';
  var eNote=vis?(p.endReason==='shadow'?'enters Earth’s shadow and vanishes':'sets below the horizon'):'drops below '+S.minEl+'°';
  return '<div class="iss-legs">'+
      leg('Appears',a.t,a.az,a.el,null,sNote)+
      leg('Highest point',pk.t,pk.az,pk.el,pk.range,'about '+dist(pk.altKm||420)+' up')+
      leg('Disappears',c.t,c.az,c.el,null,eNote)+
      '<div class="iss-skywrap"><div class="iss-l">Path across your sky</div>'+skyChart(p)+'</div>'+
    '</div>'+
    '<p class="iss-summary">'+
      (vis? 'Total time in view <b>'+mmss(p.visDurationSec)+'</b>, sky darkness at the start <b>'+darkness(p.maxSunEl)+'</b>, peak magnitude <b>'+p.mag.toFixed(1)+'</b> &mdash; '+
            (p.mag<-2?'brighter than any star in the sky.':p.mag<-0.5?'about as bright as the brightest stars.':'a modest but steady point of light.')
          : 'Above '+S.minEl+'° for '+mmss(p.durationSec)+', but not visible &mdash; either the sky is too bright or the station is in Earth’s shadow.')+
    '</p>';
}
function skyChart(p){
  var R=86, cx=100, cy=100, pal=palette();
  var src=(p.visible?p.visSamples:p.samples), pts=src;
  if(S.satrec && src.length>1){
    var a=src[0].t, b=src[src.length-1].t, obs=observerGd(), fine=[];
    for(var t=a;t<=b+1;t+=10000){ var q=sampleAt(S.satrec,obs,new Date(t),-1.8); if(q) fine.push(q); }
    if(fine.length>2) pts=fine;
  }
  function xy(az,elv){ var r=R*(90-elv)/90, ang=(az-90)*D2R; return [cx+r*Math.cos(ang), cy+r*Math.sin(ang)]; }
  var d='';
  pts.forEach(function(s,i){ var q=xy(s.az,s.el); d+=(i?'L':'M')+q[0].toFixed(1)+' '+q[1].toFixed(1); });
  var s0=xy(pts[0].az,pts[0].el), s1=xy(pts[pts.length-1].az,pts[pts.length-1].el);
  function lbl(x,y,txt){ return '<text x="'+x+'" y="'+y+'" fill="'+pal.inkSoft+'" font-size="10" font-family="'+
    'IBM Plex Mono, monospace" text-anchor="middle">'+txt+'</text>'; }
  return '<svg class="iss-sky" viewBox="0 0 200 200" role="img" aria-label="Path of the station across your sky">'+
    '<circle cx="100" cy="100" r="86" fill="'+pal.sunken+'" stroke="'+pal.rule+'"/>'+
    '<circle cx="100" cy="100" r="43" fill="none" stroke="'+pal.ruleSoft+'" stroke-dasharray="3 3"/>'+
    '<circle cx="100" cy="100" r="1.6" fill="'+pal.rule+'"/>'+
    lbl(100,11,'N')+lbl(191,104,'E')+lbl(100,196,'S')+lbl(9,104,'W')+
    '<path d="'+d+'" fill="none" stroke="'+pal.brand+'" stroke-width="2.2" stroke-linecap="round"/>'+
    '<circle cx="'+s0[0].toFixed(1)+'" cy="'+s0[1].toFixed(1)+'" r="3.6" fill="'+pal.ok+'"/>'+
    '<circle cx="'+s1[0].toFixed(1)+'" cy="'+s1[1].toFixed(1)+'" r="3.6" fill="'+pal.accent+'"/>'+
  '</svg>'+
  '<div class="iss-d" style="font-size:.7rem;color:var(--iss-ink-soft);margin-top:.4rem">'+
    '<span class="iss-sw" style="background:'+pal.ok+'"></span>appears &nbsp;'+
    '<span class="iss-sw" style="background:'+pal.accent+'"></span>disappears &nbsp;&middot; centre = overhead</div>';
}

/* =========================== LIVE =========================== */
function startLive(){
  if(S.liveTimer) clearInterval(S.liveTimer);
  renderLegend(); renderLive(); drawMap();
  S.liveTimer=setInterval(function(){ renderLive(); drawMap(); },1000);
}
function renderLegend(){
  var p=palette();
  $('iss-legend').innerHTML=
    '<span><span class="iss-sw" style="background:'+p.brand+'"></span>Station now</span>'+
    '<span><span class="iss-sw" style="background:'+p.ok+'"></span>You</span>'+
    '<span><span class="iss-sw" style="background:'+p.inkSoft+'"></span>Orbit path &plusmn;1 orbit</span>'+
    '<span><span class="iss-sw" style="background:'+p.accent+'"></span>Sunrise / sunset line</span>'+
    '<span>Dashed ring: station above the horizon</span>';
}
function nextPassText(){
  var now=Date.now();
  for(var i=0;i<S.passes.length;i++){
    var t=S.passes[i].visible?S.passes[i].visStart.t:S.passes[i].rise.t;
    if(t>now){
      var s=Math.round((t-now)/1000), h=Math.floor(s/3600), m=Math.floor(s%3600/60);
      return h>0?(h+' h '+m+' min'):(m+' min '+(s%60)+' s');
    }
  }
  return '—';
}
function renderLive(){
  if(!S.satrec) return;
  var now=new Date();
  var st=liveState(S.satrec, now, S.loc?observerGd():null);
  if(!st) return;
  var localTz=(S.loc&&S.loc.tz)||Intl.DateTimeFormat().resolvedOptions().timeZone;
  function t(tz){ return new Intl.DateTimeFormat('en-GB',{timeZone:tz,hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false}).format(now); }
  var cells=[
    ['Time — UTC', t('UTC'), 'coordinated universal time', false],
    ['Time — '+shortTz(localTz), t(localTz), localTz, false],
    ['Ground point', Math.abs(st.lat).toFixed(2)+'° '+(st.lat>=0?'N':'S')+'  '+Math.abs(st.lon).toFixed(2)+'° '+(st.lon>=0?'E':'W'), 'directly beneath the station', true],
    ['Altitude', dist(st.altKm), 'above sea level', false],
    ['Orbital speed', speed(st.speedKmS), 'one orbit every 93 minutes', false],
    ['Sunlight', st.lit?'In sunlight':'In shadow', st.lit?'reflecting, potentially visible':'dark, not visible', true]
  ];
  if(st.el!=null){
    cells.push(['In your sky', st.el>0?(Math.round(st.el)+'° up, '+compass(st.az)):'Below horizon',
      st.el>0?dist(st.range)+' away':'not above your horizon', true]);
    cells.push(['Next visible pass', nextPassText(), 'from now', false]);
  }
  $('iss-live').innerHTML=cells.map(function(c){
    return '<div class="iss-kpi"><div class="iss-l">'+c[0]+'</div>'+
      '<div class="iss-v'+(c[3]?' iss-sm':'')+'">'+c[1]+'</div>'+
      '<div class="iss-d">'+c[2]+'</div></div>';
  }).join('');
}

var trackCache={t:0,pts:null};
function drawMap(){
  if(!S.satrec) return;
  var cv=$('iss-mapc'), ctx=cv.getContext('2d'), W=cv.width, H=cv.height, pal=palette();
  var X=function(lon){ return (lon+180)/360*W; }, Y=function(lat){ return (90-lat)/180*H; };
  ctx.clearRect(0,0,W,H);
  ctx.fillStyle=pal.paper; ctx.fillRect(0,0,W,H);

  ctx.strokeStyle=pal.ruleSoft; ctx.lineWidth=1;
  for(var g=-150;g<=150;g+=30){ ctx.beginPath(); ctx.moveTo(X(g),0); ctx.lineTo(X(g),H); ctx.stroke(); }
  for(var gl=-60;gl<=60;gl+=30){ ctx.beginPath(); ctx.moveTo(0,Y(gl)); ctx.lineTo(W,Y(gl)); ctx.stroke(); }

  ctx.strokeStyle=pal.rule; ctx.fillStyle=pal.card; ctx.lineWidth=1;
  for(var i=0;i<LAND.length;i++){
    var r=LAND[i]; ctx.beginPath();
    for(var j=0;j<r.length;j+=2){ var x=X(r[j]), y=Y(r[j+1]); if(j===0) ctx.moveTo(x,y); else ctx.lineTo(x,y); }
    ctx.closePath(); ctx.fill(); ctx.stroke();
  }

  var now=new Date(), gmst=satellite.gstime(now);
  var sunGd=satellite.eciToGeodetic(sunEci(now),gmst);
  var dec=satellite.degreesLat(sunGd.latitude), slon=satellite.degreesLong(sunGd.longitude);
  if(Math.abs(dec)<0.15) dec = dec>=0?0.15:-0.15;
  var term=[];
  for(var lon=-180;lon<=180;lon+=2){
    term.push([X(lon), Y(Math.atan(-Math.cos((lon-slon)*D2R)/Math.tan(dec*D2R))*R2D)]);
  }
  function region(toBottom){
    ctx.beginPath();
    term.forEach(function(q,k){ if(k===0) ctx.moveTo(q[0],q[1]); else ctx.lineTo(q[0],q[1]); });
    if(toBottom){ ctx.lineTo(W,H); ctx.lineTo(0,H); } else { ctx.lineTo(W,0); ctx.lineTo(0,0); }
    ctx.closePath();
  }
  region(dec>0); ctx.fillStyle='rgba(0,0,0,.55)'; ctx.fill();
  region(dec<=0); ctx.fillStyle='rgba(255,255,255,.05)'; ctx.fill();
  ctx.beginPath();
  term.forEach(function(q,k){ if(k===0) ctx.moveTo(q[0],q[1]); else ctx.lineTo(q[0],q[1]); });
  ctx.strokeStyle=pal.accent; ctx.globalAlpha=.55; ctx.lineWidth=1.4; ctx.stroke(); ctx.globalAlpha=1;

  var stNow=liveState(S.satrec,now,null);
  if(!trackCache.pts || now.getTime()-trackCache.t>20000){
    var pts=[], t0=now.getTime()-46*60000;
    for(var k2=0;k2<=92;k2++){
      var d=new Date(t0+k2*60000), pv=satellite.propagate(S.satrec,d);
      if(!pv||!pv.position) continue;
      var gd=satellite.eciToGeodetic(pv.position,satellite.gstime(d));
      pts.push([satellite.degreesLong(gd.longitude),satellite.degreesLat(gd.latitude)]);
    }
    trackCache={t:now.getTime(),pts:pts};
  }
  ctx.strokeStyle=pal.inkSoft; ctx.lineWidth=1.6; ctx.beginPath();
  var prev=null;
  trackCache.pts.forEach(function(q){
    if(prev && Math.abs(q[0]-prev[0])>180){ ctx.stroke(); ctx.beginPath(); ctx.moveTo(X(q[0]),Y(q[1])); }
    else if(!prev) ctx.moveTo(X(q[0]),Y(q[1]));
    else ctx.lineTo(X(q[0]),Y(q[1]));
    prev=q;
  });
  ctx.stroke();

  var rho=Math.acos(RE/(RE+stNow.altKm))*R2D;
  ctx.strokeStyle=pal.brand; ctx.globalAlpha=.6; ctx.setLineDash([5,5]); ctx.lineWidth=1.4;
  ctx.beginPath(); var pf=null;
  for(var b2=0;b2<=360;b2+=4){
    var br=b2*D2R, la1=stNow.lat*D2R, lo1=stNow.lon*D2R, dr=rho*D2R;
    var la2=Math.asin(Math.sin(la1)*Math.cos(dr)+Math.cos(la1)*Math.sin(dr)*Math.cos(br));
    var lo2=lo1+Math.atan2(Math.sin(br)*Math.sin(dr)*Math.cos(la1),Math.cos(dr)-Math.sin(la1)*Math.sin(la2));
    var LO=((lo2*R2D+540)%360)-180, LA=la2*R2D;
    if(pf!==null && Math.abs(LO-pf)>180){ ctx.stroke(); ctx.beginPath(); ctx.moveTo(X(LO),Y(LA)); }
    else if(pf===null) ctx.moveTo(X(LO),Y(LA)); else ctx.lineTo(X(LO),Y(LA));
    pf=LO;
  }
  ctx.stroke(); ctx.setLineDash([]); ctx.globalAlpha=1;

  if(S.loc){
    ctx.fillStyle=pal.ok;
    ctx.beginPath(); ctx.arc(X(S.loc.lon),Y(S.loc.lat),4.5,0,7); ctx.fill();
    ctx.strokeStyle=pal.ok; ctx.globalAlpha=.5; ctx.lineWidth=1.4;
    ctx.beginPath(); ctx.arc(X(S.loc.lon),Y(S.loc.lat),9,0,7); ctx.stroke(); ctx.globalAlpha=1;
  }
  ctx.fillStyle=pal.brand;
  ctx.beginPath(); ctx.arc(X(stNow.lon),Y(stNow.lat),6,0,7); ctx.fill();
  ctx.strokeStyle=pal.paper; ctx.lineWidth=2; ctx.stroke();
}

/* =========================== FOOT =========================== */
function renderFoot(){
  if(!S.tleInfo || !S.tleInfo.epoch) return;
  $('iss-datanote').innerHTML='Orbit propagated in your browser with SGP4 from an element set supplied by <b>'+
    esc(S.tleInfo.source)+'</b>, epoch '+S.tleInfo.epoch.toISOString().replace('T',' ').substr(0,16)+
    ' UTC &mdash; '+S.tleInfo.ageDays.toFixed(1)+' days old.';
  if(S.tleInfo.stale){
    $('iss-banner').innerHTML='<div class="iss-alert"><span class="iss-ic">!</span><span>'+
      '<b>Using a stored backup element set.</b> No live orbit data source could be reached, so these times may be off by several minutes.</span></div>';
  }
}

/* =========================== BOOT =========================== */
(function boot(){
  if(!window.satellite){
    $('iss-passes').innerHTML='<div class="iss-empty">Could not load the orbital maths library. Please refresh the page.</div>';
    return;
  }
  loadTLE().then(function(){
    if(!S.satrec){
      $('iss-banner').innerHTML='<div class="iss-alert iss-bad"><span class="iss-ic">!</span><span>'+
        '<b>Live orbit data is unavailable right now.</b> None of the element-set services could be reached from your browser, so pass times cannot be calculated. '+
        '<button class="iss-btn iss-sec" type="button" id="iss-retry" style="min-height:34px;margin-left:.6rem">Retry</button></span></div>';
      $('iss-passes').innerHTML='<div class="iss-empty">No orbit data &mdash; nothing to show yet.</div>';
      $('iss-count').textContent='';
      $('iss-live').innerHTML='<div class="iss-kpi"><div class="iss-l">Status</div><div class="iss-v iss-sm">Offline</div></div>';
      var rb=$('iss-retry'); if(rb) rb.addEventListener('click',function(){ location.reload(); });
      return;
    }
    renderFoot(); startLive();
    var tz=Intl.DateTimeFormat().resolvedOptions().timeZone||'';
    var city=tz.split('/').pop().replace(/_/g,' ');
    fetch('https://geocoding-api.open-meteo.com/v1/search?count=1&language=en&format=json&name='+encodeURIComponent(city))
      .then(function(r){return r.json();})
      .then(function(j){
        if(j&&j.results&&j.results[0]){
          var r=j.results[0];
          $('iss-q').value=r.name;
          setLoc({name:r.name,admin:r.admin1,country:r.country,lat:r.latitude,lon:r.longitude,elev:r.elevation,tz:r.timezone});
        } else {
          $('iss-locnow').textContent='Type your town above, or tap Use my location.';
          $('iss-passes').innerHTML='<div class="iss-empty">Enter a location to see when the station passes over.</div>';
        }
      }).catch(function(){
        $('iss-locnow').textContent='Type your town above, or tap Use my location.';
        $('iss-passes').innerHTML='<div class="iss-empty">Enter a location to see when the station passes over.</div>';
      });
  });
})();
})();
